import { useCallback, useState } from 'react';
import { nationalPartFromStored, normalizeArPhone } from '@shared/phone';
import { isValidCuitDigits } from '../../../fiscal/domain-api';
import { createCustomer, findDuplicateCustomers, updateCustomerProfile } from '../../infra/customerRepository'
import type { CustomerAccountRow } from '../../domain/customerTypes';
import { useCustomerOverlayStore } from '../../store/useCustomerOverlayStore';
import { parseDecimalInput } from '../../../shared/numeric/localeNumeric';
import { showError, showInfo, showSuccess } from '../../../shared/ui/appToast';
import { presentUnexpectedFailure } from '../../../shared/sentry/presentUnexpectedFailure';

type Params = {
  canEdit: boolean;
  refreshCustomerTotalCount: () => Promise<void>;
  loadCustomers: () => Promise<void>;
};

export function useCustomerEditorFlow({ canEdit, refreshCustomerTotalCount, loadCustomers }: Params) {
  const {
    customerEditorVisible,
    setCustomerEditorVisible,
    editingCustomer,
    setEditingCustomer,
    formName,
    setFormName,
    formPhone,
    setFormPhone,
    formCuit,
    setFormCuit,
    formLimit,
    setFormLimit,
    formEmail,
    setFormEmail,
    formAddress,
    setFormAddress,
    formTaxCategory,
    setFormTaxCategory,
    formNotes,
    setFormNotes,
    formPaymentTermsDays,
    setFormPaymentTermsDays,
    formPriceListId,
    setFormPriceListId,
  } = useCustomerOverlayStore();

  const [savingCustomerForm, setSavingCustomerForm] = useState(false);
  const [discardConfirmVisible, setDiscardConfirmVisible] = useState(false);
  const [duplicateWarning, setDuplicateWarning] = useState<CustomerAccountRow[] | null>(null);
  const [pendingCreateData, setPendingCreateData] = useState<{
    name: string;
    phone: string;
    cuit: string | null;
    creditLimit: number;
    email: string | null;
    address: string | null;
    taxCategory: string;
    notes: string | null;
    paymentTermsDays: number;
    priceListId: string | null;
  } | null>(null);

  const closeCustomerEditor = useCallback(() => {
    setCustomerEditorVisible(false);
    setEditingCustomer(null);
    setDiscardConfirmVisible(false);
    setDuplicateWarning(null);
    setPendingCreateData(null);
  }, [setCustomerEditorVisible, setEditingCustomer]);

  const isEditorDirty = useCallback((): boolean => {
    if (savingCustomerForm) return false;
    if (editingCustomer) {
      return (
        formName !== editingCustomer.name ||
        formPhone !== nationalPartFromStored(editingCustomer.phone ?? '') ||
        formCuit !== (editingCustomer.cuit ?? '') ||
        formLimit !== String(editingCustomer.creditLimit ?? 0) ||
        formEmail !== (editingCustomer.email ?? '') ||
        formAddress !== (editingCustomer.address ?? '') ||
        formTaxCategory !== (editingCustomer.taxCategory ?? 'consumidor_final') ||
        formNotes !== (editingCustomer.notes ?? '') ||
        formPaymentTermsDays !== String(editingCustomer.paymentTermsDays ?? '') ||
        formPriceListId !== (editingCustomer.priceListId ?? '')
      );
    }
    return (
      formName !== '' ||
      formPhone !== '' ||
      formCuit !== '' ||
      formLimit !== '' ||
      formEmail !== '' ||
      formAddress !== '' ||
      formTaxCategory !== 'consumidor_final' ||
      formNotes !== '' ||
      formPaymentTermsDays !== '' ||
      formPriceListId !== ''
    );
  }, [editingCustomer, formCuit, formLimit, formName, formPhone, formEmail, formAddress, formTaxCategory, formNotes, formPaymentTermsDays, formPriceListId, savingCustomerForm]);

  const tryCloseCustomerEditor = useCallback(() => {
    if (savingCustomerForm) return;
    if (!isEditorDirty()) {
      closeCustomerEditor();
      return;
    }
    setDiscardConfirmVisible(true);
  }, [closeCustomerEditor, isEditorDirty, savingCustomerForm]);

  const openNewCustomerEditor = useCallback(() => {
    if (!canEdit) {
      showInfo('Modo solo lectura', 'Tu perfil no puede agregar clientes.');
      return;
    }
    setEditingCustomer(null);
    setFormName('');
    setFormPhone('');
    setFormCuit('');
    setFormLimit('');
    setFormEmail('');
    setFormAddress('');
    setFormTaxCategory('consumidor_final');
    setFormNotes('');
    setFormPaymentTermsDays('');
    setFormPriceListId('');
    setDiscardConfirmVisible(false);
    setDuplicateWarning(null);
    setPendingCreateData(null);
    setCustomerEditorVisible(true);
  }, [canEdit, setCustomerEditorVisible, setEditingCustomer, setFormCuit, setFormLimit, setFormName, setFormPhone, setFormEmail, setFormAddress, setFormTaxCategory, setFormNotes, setFormPaymentTermsDays, setFormPriceListId]);

  const openEditCustomerEditor = useCallback(
    (customer: CustomerAccountRow) => {
      if (!canEdit) {
        showInfo('Modo solo lectura', 'Tu perfil no puede editar clientes.');
        return;
      }
      setEditingCustomer(customer);
      setFormName(customer.name);
      setFormPhone(nationalPartFromStored(customer.phone ?? ''));
      setFormCuit(customer.cuit ?? '');
      setFormLimit(customer.creditLimit ? String(customer.creditLimit) : '');
      setFormEmail(customer.email ?? '');
      setFormAddress(customer.address ?? '');
      setFormTaxCategory(customer.taxCategory ?? 'consumidor_final');
      setFormNotes(customer.notes ?? '');
      setFormPaymentTermsDays(customer.paymentTermsDays != null ? String(customer.paymentTermsDays) : '');
      setFormPriceListId(customer.priceListId ?? '');
      setDiscardConfirmVisible(false);
      setDuplicateWarning(null);
      setPendingCreateData(null);
      setCustomerEditorVisible(true);
    },
    [canEdit, setCustomerEditorVisible, setEditingCustomer, setFormCuit, setFormLimit, setFormName, setFormPhone, setFormEmail, setFormAddress, setFormTaxCategory, setFormNotes, setFormPaymentTermsDays, setFormPriceListId],
  );

  const saveCustomerEditor = useCallback(async () => {
    const limitRaw = String(formLimit).trim();
    const limit = limitRaw === '' ? 0 : parseDecimalInput(limitRaw, { min: 0 });
    if (!formName.trim()) {
      showError('Datos incompletos', 'Ingresá el nombre del cliente.');
      return;
    }
    if (limit === null) {
      showError('Datos inválidos', 'Revisá el límite de crédito (ej. 5000 o 5000,50).');
      return;
    }
    const cuitTrim = formCuit.trim();
    if (cuitTrim && !isValidCuitDigits(cuitTrim)) {
      showError('Datos inválidos', 'Revisá el CUIT (debe tener 11 dígitos).');
      return;
    }
    const emailTrim = formEmail.trim();
    if (emailTrim && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(emailTrim)) {
      showError('Datos inválidos', 'Revisá el email (ej. nombre@dominio.com).');
      return;
    }
    const phoneTrim = formPhone.trim();
    let phone: string | null = null;
    if (phoneTrim) {
      phone = normalizeArPhone(phoneTrim);
      if (!phone) {
        showError('Datos inválidos', 'Revisá el teléfono (incluí área, ej. 11 5555-5555).');
        return;
      }
    }
    const paymentDays = formPaymentTermsDays.trim() === '' ? 0 : parseInt(formPaymentTermsDays.trim(), 10);
    const createData = {
      name: formName.trim(),
      phone: phone ?? '',
      cuit: cuitTrim || null,
      creditLimit: limit,
      email: emailTrim || null,
      address: formAddress.trim() || null,
      taxCategory: formTaxCategory || 'consumidor_final',
      notes: formNotes.trim() || null,
      paymentTermsDays: Number.isFinite(paymentDays) ? paymentDays : 0,
      priceListId: formPriceListId.trim() || null,
    } as const;

    try {
      setSavingCustomerForm(true);
      if (editingCustomer) {
        await updateCustomerProfile(editingCustomer.id, {
          name: createData.name,
          phone: createData.phone,
          cuit: createData.cuit,
          creditLimit: createData.creditLimit,
          email: createData.email,
          address: createData.address,
          taxCategory: createData.taxCategory,
          notes: createData.notes,
          paymentTermsDays: createData.paymentTermsDays,
          priceListId: createData.priceListId,
        });
        showSuccess('Listo', 'Cliente actualizado.');
        closeCustomerEditor();
        await refreshCustomerTotalCount();
        await loadCustomers();
      } else {
        // Verificar duplicados antes de crear (solo si no hay warning previo).
        if (!duplicateWarning) {
          const dupes = await findDuplicateCustomers({
            phone: createData.phone || null,
            name: createData.name,
          });
          if (dupes.length > 0) {
            setDuplicateWarning(dupes);
            setPendingCreateData(createData);
            return;
          }
        }
        await createCustomer(createData);
        showSuccess('Listo', 'Cliente creado.');
        closeCustomerEditor();
        await refreshCustomerTotalCount();
        await loadCustomers();
      }
    } catch (error: unknown) {
      presentUnexpectedFailure({ module: 'customers', op: 'customer_editor.save', error, userMessage: 'No se pudo guardar el cliente.' });
    } finally {
      setSavingCustomerForm(false);
    }
  }, [
    closeCustomerEditor,
    duplicateWarning,
    editingCustomer,
    formCuit,
    formLimit,
    formName,
    formPhone,
    formEmail,
    formAddress,
    formTaxCategory,
    formNotes,
    formPaymentTermsDays,
    formPriceListId,
    loadCustomers,
    refreshCustomerTotalCount,
  ]);

  const confirmCreateAnyway = useCallback(async () => {
    if (!pendingCreateData) return;
    try {
      setSavingCustomerForm(true);
      await createCustomer(pendingCreateData);
      showSuccess('Listo', 'Cliente creado.');
      closeCustomerEditor();
      await refreshCustomerTotalCount();
      await loadCustomers();
    } catch (error: unknown) {
      presentUnexpectedFailure({ module: 'customers', op: 'customer_editor.save', error, userMessage: 'No se pudo guardar el cliente.' });
    } finally {
      setSavingCustomerForm(false);
    }
  }, [closeCustomerEditor, loadCustomers, pendingCreateData, refreshCustomerTotalCount]);

  const dismissDuplicateWarning = useCallback(() => {
    setDuplicateWarning(null);
    setPendingCreateData(null);
  }, []);

  return {
    customerEditorVisible,
    editingCustomer,
    formName,
    formPhone,
    formCuit,
    formLimit,
    formEmail,
    formAddress,
    formTaxCategory,
    formNotes,
    formPaymentTermsDays,
    formPriceListId,
    setFormName,
    setFormPhone,
    setFormCuit,
    setFormLimit,
    setFormEmail,
    setFormAddress,
    setFormTaxCategory,
    setFormNotes,
    setFormPaymentTermsDays,
    setFormPriceListId,
    savingCustomerForm,
    discardConfirmVisible,
    setDiscardConfirmVisible,
    duplicateWarning,
    openNewCustomerEditor,
    openEditCustomerEditor,
    tryCloseCustomerEditor,
    closeCustomerEditor,
    saveCustomerEditor,
    confirmCreateAnyway,
    dismissDuplicateWarning,
  };
}
